import { DuctType, type Damper as DamperType, type Fan as FanType } from "@eisox/api/core-api";
import { cn } from "@eisox/design-system";

import { m } from "~/paraglide/messages";

import { hasDuctReading, type DuctSensors } from "../helpers";
import { Direction } from "../types";
import { Damper } from "./damper";
import { DamperDialog } from "./damper-dialog";
import { Fan } from "./fan";
import { FanDialog } from "./fan-dialog";
import { Filter } from "./filter";
import { Outlet } from "./outlet";
import * as RecirculationDamper from "./recirculation-damper";
import { Sensors } from "./sensors";

interface ReturnProps {
  buildingId: string;
  airHandlerUnitId: string;
  temperature?: number;
  temperatureDefect?: boolean;
  setpoint?: number;
  actualSetpoint?: number;
  coolingSetpoint?: number;
  coolingActualSetpoint?: number;
  fan?: FanType;
  airFilterPressureSwitchDefect?: boolean;
  sensors?: DuctSensors;
  damper?: DamperType;
  recirculationDamper?: DamperType;
}

const Return: React.FC<ReturnProps> = ({
  buildingId,
  airHandlerUnitId,
  temperature,
  temperatureDefect,
  setpoint,
  actualSetpoint,
  coolingSetpoint,
  coolingActualSetpoint,
  fan,
  airFilterPressureSwitchDefect,
  sensors,
  damper,
  recirculationDamper,
}) => {
  return (
    <div
      className={cn(
        `gap-2.5 pl-2.5 after:right-16.25 after:h-23.5 relative flex flex-row items-center justify-end [grid-area:return] *:shrink-0 *:self-end after:absolute after:-bottom-px after:z-[-2] after:w-[calc(100%-65px)] after:border-t after:border-b after:border-t-primary-dark-gray after:border-b-primary-dark-gray after:bg-neutral-100 after:content-['']`,
      )}
    >
      {recirculationDamper && (
        <DamperDialog buildingId={buildingId} airHandlerUnitId={airHandlerUnitId} damper={recirculationDamper}>
          <RecirculationDamper.Damper {...recirculationDamper} />
        </DamperDialog>
      )}
      {fan && (
        <FanDialog buildingId={buildingId} airHandlerUnitId={airHandlerUnitId} fan={fan}>
          <Fan {...fan} direction={Direction.LEFT} />
        </FanDialog>
      )}
      {airFilterPressureSwitchDefect !== undefined && (
        <Filter defect={airFilterPressureSwitchDefect} label={m.air_handler_unit_filter_return()} />
      )}
      {sensors && hasDuctReading(sensors) && (
        <Sensors buildingId={buildingId} airHandlerUnitId={airHandlerUnitId} {...sensors} />
      )}
      {damper && (
        <DamperDialog buildingId={buildingId} airHandlerUnitId={airHandlerUnitId} damper={damper}>
          <Damper {...damper} />
        </DamperDialog>
      )}
      <Outlet
        buildingId={buildingId}
        airHandlerUnitId={airHandlerUnitId}
        type={DuctType.RETURN}
        temperature={temperature}
        temperatureDefect={temperatureDefect}
        setpoint={setpoint}
        actualSetpoint={actualSetpoint}
        coolingSetpoint={coolingSetpoint}
        coolingActualSetpoint={coolingActualSetpoint}
      />
    </div>
  );
};

Return.displayName = "Return";

export { Return };
export type { ReturnProps };
